/**
 * 단일 쓰기 경로 (계획서 §2, §8, §17).
 *
 *   validate(zod strict) → domain 불변식 → revision+1 → 암호화 → 하나의 IndexedDB 트랜잭션
 *   (entity + history + audit + outbox)
 *
 * - 암호화는 트랜잭션 밖에서 먼저 끝낸다 (IDB 트랜잭션은 await 중 자동 커밋되기 때문).
 * - 트랜잭션 안에서 저장된 revision을 다시 확인한다 → 다른 탭/기기의 변경을 조용히 덮어쓰지 않는다.
 * - 중간 실패 시 트랜잭션 전체가 취소된다 → 반쯤 쓰인 기록이 없다.
 * - 삭제는 soft-delete. 모든 revision은 history에 남는다.
 */
import { uuidv7 } from 'uuidv7';
import {
  ENTITY_SCHEMAS,
  SCHEMA_VERSION,
  type Actor,
  type AnyEntity,
  type EntityOf,
  type EntityType,
} from '@/domain/schema/v1';
import { changedContentFields, checkWrite } from '@/domain/rules/writeRules';
import { openJson, sealJson, type DataKey } from '../crypto/envelope';
import type { CounselDb, StoredRecord } from '../db/schema';
import {
  InvariantError,
  isQuotaError,
  NotFoundError,
  RevisionConflictError,
  StorageFullError,
  ValidationError,
  IntegrityError,
} from '../errors';

const BASE_KEYS = [
  'id',
  'entityType',
  'schemaVersion',
  'revision',
  'createdAt',
  'updatedAt',
  'createdBy',
  'updatedBy',
  'lastModifiedDevice',
  'deletedAt',
] as const;
type BaseKey = (typeof BASE_KEYS)[number];

/** entity의 내용 필드 (메타 제외) */
export type Draft<T extends EntityType> = Omit<EntityOf<T>, BaseKey>;

export type RepoContext = {
  db: CounselDb;
  dataKey: DataKey;
  vaultId: string;
  deviceId: string;
  now?: () => Date;
  /** 테스트 전용 장애 주입 */
  faults?: { beforeCommit?: () => void };
};

type AuditAction = 'create' | 'update' | 'approve' | 'revise' | 'delete' | 'restore';

export function recordAad(vaultId: string, r: Pick<StoredRecord, 'entityType' | 'id' | 'revision' | 'schemaVersion'>) {
  return `counselai|v1|${vaultId}|${r.entityType}|${r.id}|${r.revision}|${r.schemaVersion}`;
}

const kebab = (s: string) => s.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

function indexFields(e: AnyEntity): { clientId: string; sessionId: string } {
  const r = e as Record<string, unknown>;
  const clientId = e.entityType === 'Client' ? e.id : typeof r.clientId === 'string' ? r.clientId : '';
  const sessionId = e.entityType === 'Session' ? e.id : typeof r.sessionId === 'string' ? r.sessionId : '';
  return { clientId, sessionId };
}

function stripUndefined<T extends Record<string, unknown>>(o: T): T {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T;
}

function isApproval(prev: AnyEntity | null, next: AnyEntity): boolean {
  const s = (e: AnyEntity | null) => (e && 'status' in e ? e.status : undefined);
  if (s(next) === 'approved' && s(prev) !== 'approved') return true;
  if (
    next.entityType === 'RiskAssessment' &&
    next.counselorApproved &&
    !(prev as typeof next | null)?.counselorApproved
  )
    return true;
  return false;
}

export class Repository {
  constructor(private readonly ctx: RepoContext) {}

  private now() {
    return (this.ctx.now?.() ?? new Date()).toISOString();
  }

  // ---------- 읽기 ----------

  async decryptRecord<T extends EntityType = EntityType>(rec: StoredRecord): Promise<EntityOf<T>> {
    const raw = await openJson<Record<string, unknown>>(this.ctx.dataKey, rec.env, recordAad(this.ctx.vaultId, rec));
    if (raw.id !== rec.id || raw.entityType !== rec.entityType || raw.revision !== rec.revision) {
      throw new IntegrityError('레코드 정체성 불일치');
    }
    const parsed = ENTITY_SCHEMAS[rec.entityType].safeParse(raw);
    if (!parsed.success) throw new ValidationError(parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.code}`));
    return parsed.data as EntityOf<T>;
  }

  async get<T extends EntityType>(type: T, id: string, opts: { includeDeleted?: boolean } = {}): Promise<EntityOf<T>> {
    const rec = await this.ctx.db.entities.get([type, id]);
    if (!rec || (rec.deleted && !opts.includeDeleted)) throw new NotFoundError(type, id);
    return this.decryptRecord<T>(rec);
  }

  async find<T extends EntityType>(type: T, id: string): Promise<EntityOf<T> | null> {
    try {
      return await this.get(type, id);
    } catch (e) {
      if (e instanceof NotFoundError) return null;
      throw e;
    }
  }

  async list<T extends EntityType>(
    type: T,
    filter: { clientId?: string; sessionId?: string; includeDeleted?: boolean } = {},
  ): Promise<EntityOf<T>[]> {
    const t = this.ctx.db.entities;
    const rows = filter.sessionId
      ? await t.where('[entityType+sessionId]').equals([type, filter.sessionId]).toArray()
      : filter.clientId
        ? await t.where('[entityType+clientId]').equals([type, filter.clientId]).toArray()
        : await t.where('entityType').equals(type).toArray();
    const visible = rows.filter((r) => filter.includeDeleted || !r.deleted);
    return Promise.all(visible.map((r) => this.decryptRecord<T>(r)));
  }

  async history<T extends EntityType>(type: T, id: string): Promise<EntityOf<T>[]> {
    const rows = await this.ctx.db.history.where('[entityType+id]').equals([type, id]).sortBy('revision');
    return Promise.all(rows.map((r) => this.decryptRecord<T>(r)));
  }

  // ---------- 쓰기 ----------

  /**
   * 생성. id를 주면 멱등: 같은 id·같은 내용이 이미 있으면 그대로 반환 (재시도 시 중복 생성 방지).
   */
  async create<T extends EntityType>(
    type: T,
    draft: Draft<T>,
    actor: Actor,
    opts: { id?: string } = {},
  ): Promise<EntityOf<T>> {
    const id = opts.id ?? uuidv7();
    const at = this.now();
    const entity = stripUndefined({
      ...(draft as Record<string, unknown>),
      id,
      entityType: type,
      schemaVersion: SCHEMA_VERSION,
      revision: 1,
      createdAt: at,
      updatedAt: at,
      createdBy: actor,
      updatedBy: actor,
      lastModifiedDevice: this.ctx.deviceId,
    }) as unknown as AnyEntity;

    if (opts.id) {
      const existing = await this.ctx.db.entities.get([type, id]);
      if (existing) {
        const cur = await this.decryptRecord<T>(existing);
        const { content: a } = splitBase(cur as AnyEntity);
        const { content: b } = splitBase(entity);
        if (JSON.stringify(sortKeys(a)) === JSON.stringify(sortKeys(b))) return cur;
        throw new RevisionConflictError(type, id, 0, existing.revision);
      }
    }
    return (await this.commit(null, 0, entity, actor, 'create')) as EntityOf<T>;
  }

  /**
   * 수정 (필드 단위 patch).
   * expectedRevision을 주면, 그 이후 다른 곳에서 바뀐 필드와 이 patch의 필드가 겹치는지 본다.
   *   - 겹치지 않으면 최신 내용 위에 patch를 적용한다 (SYNC_SECURITY §9: 서로 다른 필드 수정은 자동 병합)
   *   - 겹치면 RevisionConflictError (조용한 덮어쓰기 금지)
   * 커밋 순간의 경쟁으로 실패하면 같은 규칙으로 몇 번 다시 시도한다.
   */
  async update<T extends EntityType>(
    type: T,
    id: string,
    patch: Partial<Draft<T>>,
    actor: Actor,
    opts: { expectedRevision?: number; action?: AuditAction } = {},
  ): Promise<EntityOf<T>> {
    const patchKeys = Object.keys(patch as Record<string, unknown>);
    for (let attempt = 0; ; attempt++) {
      const rec = await this.ctx.db.entities.get([type, id]);
      if (!rec) throw new NotFoundError(type, id);
      const prev = (await this.decryptRecord<T>(rec)) as AnyEntity;
      const expected = opts.expectedRevision ?? rec.revision;
      if (expected !== rec.revision) {
        const baseRow = await this.ctx.db.history.get([type, id, expected]);
        if (!baseRow) throw new RevisionConflictError(type, id, expected, rec.revision);
        const base = (await this.decryptRecord<T>(baseRow)) as AnyEntity;
        const changedElsewhere = changedContentFields(base, prev);
        if (changedElsewhere.some((k) => patchKeys.includes(k)) || !!base.deletedAt !== !!prev.deletedAt) {
          throw new RevisionConflictError(type, id, expected, rec.revision);
        }
      }
      const next = stripUndefined({
        ...(prev as Record<string, unknown>),
        ...(patch as Record<string, unknown>),
        revision: rec.revision + 1,
        updatedAt: this.now(),
        updatedBy: actor,
        lastModifiedDevice: this.ctx.deviceId,
      }) as unknown as AnyEntity;
      const action = opts.action ?? (isApproval(prev, next) ? 'approve' : 'update');
      try {
        return (await this.commit(prev, rec.revision, next, actor, action)) as EntityOf<T>;
      } catch (e) {
        // 같은 기기 안의 여러 자동저장이 동시에 커밋을 다툴 수 있다. 규칙은 매번 다시 검사한다.
        if (e instanceof RevisionConflictError && attempt < 10) continue;
        throw e;
      }
    }
  }

  /**
   * 동기화 병합·충돌 해결용: 지정한 revision 번호로 새 버전을 커밋한다 (다른 기기 revision과 번호가 겹치지 않게).
   * 불변식 검사·암호화·감사·history는 일반 쓰기와 동일하게 적용된다.
   */
  async commitVersion(
    prev: AnyEntity,
    content: Record<string, unknown>,
    revision: number,
    actor: Actor,
  ): Promise<AnyEntity> {
    if (revision <= prev.revision) throw new Error('revision은 현재보다 커야 합니다');
    const next = stripUndefined({
      ...content,
      id: prev.id,
      entityType: prev.entityType,
      schemaVersion: prev.schemaVersion,
      createdAt: prev.createdAt,
      createdBy: prev.createdBy,
      revision,
      updatedAt: this.now(),
      updatedBy: actor,
      lastModifiedDevice: this.ctx.deviceId,
    }) as unknown as AnyEntity;
    return this.commit(prev, prev.revision, next, actor, 'update');
  }

  async softDelete(type: EntityType, id: string, opts: { expectedRevision?: number } = {}): Promise<void> {
    await this.setDeleted(type, id, this.now(), opts.expectedRevision, 'delete');
  }

  async restore(type: EntityType, id: string, opts: { expectedRevision?: number } = {}): Promise<void> {
    await this.setDeleted(type, id, null, opts.expectedRevision, 'restore');
  }

  private async setDeleted(
    type: EntityType,
    id: string,
    deletedAt: string | null,
    expectedRevision: number | undefined,
    action: AuditAction,
  ) {
    const rec = await this.ctx.db.entities.get([type, id]);
    if (!rec) throw new NotFoundError(type, id);
    const prev = await this.decryptRecord(rec);
    const expected = expectedRevision ?? rec.revision;
    if (expected !== rec.revision) throw new RevisionConflictError(type, id, expected, rec.revision);
    const next = {
      ...prev,
      deletedAt,
      revision: rec.revision + 1,
      updatedAt: this.now(),
      updatedBy: 'user' as const,
      lastModifiedDevice: this.ctx.deviceId,
    } as AnyEntity;
    await this.commit(prev, rec.revision, next, 'user', action);
  }

  /**
   * 손상 복구: 현재 레코드를 읽을 수 없을 때 history의 마지막 정상 revision 내용으로 새 revision을 만든다.
   * 손상된 행을 덮어쓰지 않고 앞으로 진행하며, 어느 revision에서 복구했는지 반환한다.
   */
  async recoverFromHistory(type: EntityType, id: string): Promise<{ recoveredFromRevision: number }> {
    const rec = await this.ctx.db.entities.get([type, id]);
    if (!rec) throw new NotFoundError(type, id);
    const rows = (await this.ctx.db.history.where('[entityType+id]').equals([type, id]).toArray()).sort(
      (a, b) => b.revision - a.revision,
    );
    for (const row of rows) {
      let good: AnyEntity;
      try {
        good = (await this.decryptRecord(row)) as AnyEntity;
      } catch {
        continue;
      }
      if (row.revision === rec.revision) {
        // 같은 revision의 정상 사본: entity 행만 교체 (내용 동일)
        await this.ctx.db.entities.put({ ...rec, env: row.env });
        return { recoveredFromRevision: row.revision };
      }
      const next = {
        ...good,
        revision: rec.revision + 1,
        updatedAt: this.now(),
        updatedBy: 'system' as const,
        lastModifiedDevice: this.ctx.deviceId,
      } as AnyEntity;
      await this.commit(null, rec.revision, next, 'system', 'restore', { skipPrevCheck: true });
      return { recoveredFromRevision: row.revision };
    }
    throw new IntegrityError('복구 가능한 revision이 없습니다');
  }

  // ---------- 커밋 ----------

  private async commit(
    prev: AnyEntity | null,
    expectedStoredRevision: number,
    next: AnyEntity,
    actor: Actor,
    action: AuditAction,
    opts: { skipPrevCheck?: boolean } = {},
  ): Promise<AnyEntity> {
    const parsed = ENTITY_SCHEMAS[next.entityType].safeParse(next);
    if (!parsed.success) throw new ValidationError(parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.code}`));
    const entity = parsed.data as AnyEntity;

    if (!opts.skipPrevCheck) {
      const violations = checkWrite(prev, entity, actor);
      if (violations.length) throw new InvariantError(violations);
    }

    const { db, dataKey, vaultId } = this.ctx;
    const record: StoredRecord = {
      entityType: entity.entityType,
      id: entity.id,
      ...indexFields(entity),
      revision: entity.revision,
      schemaVersion: entity.schemaVersion,
      updatedAt: entity.updatedAt,
      deleted: entity.deletedAt ? 1 : 0,
      env: await sealJson(dataKey, entity, recordAad(vaultId, entity)),
    };

    // 감사 기록도 같은 트랜잭션에 (AuditEvent 자체에는 audit를 남기지 않는다)
    let auditRecord: StoredRecord | null = null;
    if (entity.entityType !== 'AuditEvent') {
      const idx = indexFields(entity);
      const at = this.now();
      const audit = ENTITY_SCHEMAS.AuditEvent.parse(
        stripUndefined({
          id: uuidv7(),
          entityType: 'AuditEvent',
          schemaVersion: SCHEMA_VERSION,
          revision: 1,
          createdAt: at,
          updatedAt: at,
          createdBy: 'system',
          updatedBy: 'system',
          lastModifiedDevice: this.ctx.deviceId,
          clientId: idx.clientId || undefined,
          sessionId: idx.sessionId || undefined,
          targetEntityType: entity.entityType,
          targetEntityId: entity.id,
          action,
          summary: `${kebab(entity.entityType)}.${action}`,
          previousRevision: expectedStoredRevision,
          newRevision: entity.revision,
        }),
      );
      auditRecord = {
        entityType: 'AuditEvent',
        id: audit.id,
        clientId: audit.clientId ?? '',
        sessionId: audit.sessionId ?? '',
        revision: 1,
        schemaVersion: SCHEMA_VERSION,
        updatedAt: at,
        deleted: 0,
        env: await sealJson(dataKey, audit, recordAad(vaultId, audit)),
      };
    }

    try {
      await db.transaction('rw', db.entities, db.history, db.outbox, async () => {
        const current = await db.entities.get([entity.entityType, entity.id]);
        const actual = current?.revision ?? 0;
        if (actual !== expectedStoredRevision) {
          throw new RevisionConflictError(entity.entityType, entity.id, expectedStoredRevision, actual);
        }
        await db.entities.put(record);
        await db.history.put(record);
        await db.outbox.add({
          entityType: record.entityType,
          id: record.id,
          revision: record.revision,
          queuedAt: record.updatedAt,
        });
        if (auditRecord) {
          await db.entities.put(auditRecord);
          await db.history.put(auditRecord);
          await db.outbox.add({
            entityType: 'AuditEvent',
            id: auditRecord.id,
            revision: 1,
            queuedAt: auditRecord.updatedAt,
          });
        }
        this.ctx.faults?.beforeCommit?.();
      });
    } catch (e) {
      if (isQuotaError(e)) throw new StorageFullError();
      throw e;
    }
    // 화면(진행 막대 등)이 바로 따라오도록 알림. 종류만 보내고 내용은 보내지 않는다
    if (typeof window !== 'undefined')
      window.dispatchEvent(new CustomEvent('counselai:data-changed', { detail: { entityType: entity.entityType } }));
    return entity;
  }
}

function splitBase(e: AnyEntity): { content: Record<string, unknown> } {
  const r = { ...(e as Record<string, unknown>) };
  for (const k of BASE_KEYS) delete r[k];
  return { content: r };
}

function sortKeys(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sortKeys);
  if (v && typeof v === 'object')
    return Object.fromEntries(
      Object.entries(v as Record<string, unknown>)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, x]) => [k, sortKeys(x)]),
    );
  return v;
}
